import { Injectable, signal, OnDestroy, inject, computed } from '@angular/core';
import { DeviceType, DeviceInfo, MediaQueryInfo, DeviceDetectionConfig, DeviceBusinessInfo } from './device-detection.interface';
import { DEVICE_BREAKPOINTS, MEDIA_QUERIES } from './device-detection.constants';
import { LayoutStrategiesRegistry } from './layout-strategies.registry';


@Injectable({
    providedIn: 'root'
})
export class DeviceDetectionService implements OnDestroy {
    private readonly layoutStrategies = inject(LayoutStrategiesRegistry);

    // Private signals
    private _deviceType = signal<DeviceType>(DeviceType.desktop);
    private _windowWidth = signal<number>(0);
    private _windowHeight = signal<number>(0);
    private _mediaQueries = signal<MediaQueryInfo>({
        mobile: false,
        tablet: false,
        desktop: true
    });

    // Public computed signals
    readonly deviceType = this._deviceType.asReadonly();
    readonly windowWidth = this._windowWidth.asReadonly();
    readonly windowHeight = this._windowHeight.asReadonly();
    readonly mediaQueries = this._mediaQueries.asReadonly();

    // Computed device type signals
    readonly isMobile = computed(() => this._deviceType() === DeviceType.mobile);
    readonly isTablet = computed(() => this._deviceType() === DeviceType.tablet);
    readonly isDesktop = computed(() => this._deviceType() === DeviceType.desktop);

    // Legacy breakpoint signals for backward compatibility
    readonly isMobileMd = computed(() => this._windowWidth() <= 989);
    readonly isMobileSm = computed(() => this._windowWidth() <= 768);
    readonly isMobileLegacy = computed(() => this._windowWidth() <= 640);

    // Media query listeners
    private mediaQueryListeners: MediaQueryList[] = [];
    private resizeListener?: () => void;

    constructor() {
        this.initializeDeviceDetection();
    }

    ngOnDestroy(): void {
        this.cleanup();
    }

    /**
     * Initialize device detection
     */
    private initializeDeviceDetection(): void {
        if (typeof window !== 'undefined') {
            this.detectDeviceType();
            this.setupMediaQueryListeners();
            this.setupResizeListener();
        }
    }

    /**
     * Detect device type based on window width
     */
    private detectDeviceType(): void {
        const width = window.innerWidth;

        if (width <= DEVICE_BREAKPOINTS.MOBILE) {
            this._deviceType.set(DeviceType.mobile);
        } else if (width <= DEVICE_BREAKPOINTS.TABLET) {
            this._deviceType.set(DeviceType.tablet);
        } else {
            this._deviceType.set(DeviceType.desktop);
        }

        this._windowWidth.set(width);
        this._windowHeight.set(window.innerHeight);
    }

    /**
     * Setup media query listeners
     */
    private setupMediaQueryListeners(): void {
        const mobileQuery = window.matchMedia(MEDIA_QUERIES.MOBILE);
        const tabletQuery = window.matchMedia(MEDIA_QUERIES.TABLET);
        const desktopQuery = window.matchMedia(MEDIA_QUERIES.DESKTOP);

        this.mediaQueryListeners = [mobileQuery, tabletQuery, desktopQuery];

        // Add listeners
        mobileQuery.addEventListener('change', this.handleMediaQueryChange.bind(this));
        tabletQuery.addEventListener('change', this.handleMediaQueryChange.bind(this));
        desktopQuery.addEventListener('change', this.handleMediaQueryChange.bind(this));

        // Initial update
        this.updateMediaQueries();
    }

    /**
     * Handle media query changes
     */
    private handleMediaQueryChange(): void {
        this.updateMediaQueries();
        this.detectDeviceType();
    }

    /**
     * Update media query signals
     */
    private updateMediaQueries(): void {
        this._mediaQueries.set({
            mobile: this.mediaQueryListeners[0]?.matches || false,
            tablet: this.mediaQueryListeners[1]?.matches || false,
            desktop: this.mediaQueryListeners[2]?.matches || false
        });
    }

    /**
     * Setup resize listener with debounce
     */
    private setupResizeListener(): void {
        let timeoutId: number;

        this.resizeListener = () => {
            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                this.detectDeviceType();
            }, 100); // 100ms debounce
        };

        window.addEventListener('resize', this.resizeListener);
    }

    /**
     * Cleanup listeners
     */
    private cleanup(): void {
        this.mediaQueryListeners.forEach(listener => {
            listener.removeEventListener('change', this.handleMediaQueryChange.bind(this));
        });

        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }
    }

    /**
     * Handle device type change with business logic
     */
    handleDeviceTypeChange(deviceType: DeviceType): void {
        this._deviceType.set(deviceType);
    }

    /**
     * Get device info with business logic recommendations using strategy pattern
     */
    getLayoutBusinessInfo(layoutId: string): DeviceBusinessInfo {
        const currentType = this._deviceType();
        return this.layoutStrategies.getBusinessInfo(layoutId, currentType);
    }

    /**
     * Get generic device business info (for backward compatibility)
     */
    getDeviceBusinessInfo(): DeviceBusinessInfo {
        return this.getLayoutBusinessInfo('layout-9'); // Use layout-9 as default
    }

    /**
     * Force device type update (for external control)
     */
    setDeviceType(deviceType: DeviceType): void {
        this.handleDeviceTypeChange(deviceType);
    }

    /**
     * Handle external device type update (for compatibility with existing code)
     */
    updateDeviceTypeFromExternal(deviceType: string): void {
        const enumDeviceType = deviceType as DeviceType;
        if (Object.values(DeviceType).includes(enumDeviceType)) {
            this.handleDeviceTypeChange(enumDeviceType);
        } else {
            // Invalid device type
        }
    }

    /**
     * Get current device info
     */
    getDeviceInfo(): DeviceInfo {
        return {
            type: this._deviceType(),
            width: this._windowWidth(),
            height: this._windowHeight(),
            isMobile: this.isMobile(),
            isTablet: this.isTablet(),
            isDesktop: this.isDesktop()
        };
    }


} 